import { Autocomplete, Chip, TextField } from "@mui/material";
import * as React from "react";

interface TagsAutocompleteProps {
  value: string[];
  onChange: (next: string[]) => void;
  options: string[];
  label?: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
}

/**
 * Multi-value tag input backed by MUI's Autocomplete in ``multiple freeSolo``
 * mode. Adds one behavior past the MUI defaults: **blur commits pending
 * text** — if the user types a value and clicks Save (or Tabs out) without
 * pressing Enter, the typed text is added as a chip before the click
 * handler reads `value`. Browser event ordering (blur fires before click on
 * the Save button) makes this a single source of truth — consumers can read
 * `value` after Save fires and trust that the typed-but-not-Enter'd tag is
 * already in it.
 */
export function TagsAutocomplete({
  value,
  onChange,
  options,
  label = "Tags",
  placeholder = "Add a tag",
  helperText,
  disabled = false,
}: TagsAutocompleteProps) {
  const [inputValue, setInputValue] = React.useState("");

  function commitPending() {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      return;
    }
    if (value.includes(trimmed)) {
      setInputValue("");
      return;
    }
    onChange([...value, trimmed]);
    setInputValue("");
  }

  return (
    <Autocomplete
      multiple
      freeSolo
      disabled={disabled}
      options={options}
      value={value}
      onChange={(_event, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_event, next, reason) => {
        // "reset" fires after a chip is created; clear the textbox so a
        // freshly-committed tag doesn't echo back.
        if (reason === "reset") {
          setInputValue("");
        } else {
          setInputValue(next);
        }
      }}
      onBlur={commitPending}
      renderTags={(items, getTagProps) =>
        items.map((option, index) => {
          // MUI's getTagProps already provides a stable key — destructure it
          // out so React doesn't warn about a missing key prop.
          const { key, ...chipProps } = getTagProps({ index });
          return <Chip key={key} size="small" label={option} {...chipProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
        />
      )}
    />
  );
}
